import { toPlainText } from '../components/jsonLd/jsonLd.comp';

export const SITE_URL = 'https://www.bracketsninja.com';

export interface IBreadcrumbCrumb {
    name: string;
    path: string;
}

/**
 * Human labels for the path segments that are not themselves a page with a
 * name we can read, plus the few whose stored name reads badly in a trail.
 */
const SEGMENT_LABELS: Record<string, string> = {
    'vertical': 'Sports & Verticals',
    'types': 'Bracket Types',
    'blog': 'Blog',
    'platforms': 'Platforms',
    'e-sports': 'E-Sports',
    'american-leagues': 'American Leagues',
    'international': 'International',
    'tennis': 'Tennis',
    'us-open': 'US Open',
};

const titleCase = (segment: string) => segment
    .split('-')
    .filter(Boolean)
    .map((word) => (word.length <= 3 && word === word.toLowerCase() && /^(nba|nfl|nhl|mlb|wnba|ufc|afc|nfc)$/.test(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

export const labelForSegment = (segment: string) =>
    SEGMENT_LABELS[segment] || titleCase(segment);

/**
 * Builds a BreadcrumbList from a path, using real page names for any ancestor
 * we were able to look up and a readable fallback for the rest.
 *
 * Google treats `item` on the last entry as optional and falls back to the
 * containing page's URL, but naming it explicitly is unambiguous, so we do.
 */
export const buildBreadcrumbs = (
    slug: string,
    currentName: string,
    ancestorNames: Record<string, string> = {},
) => {
    const clean = (slug || '').replace(/^\/+|\/+$/g, '');
    if (!clean) return null;

    const segments = clean.split('/').filter(Boolean);
    const crumbs: IBreadcrumbCrumb[] = [{ name: 'Home', path: '' }];

    segments.forEach((segment, index) => {
        const path = segments.slice(0, index + 1).join('/');
        const isLast = index === segments.length - 1;
        const name = isLast
            ? (toPlainText(currentName) || labelForSegment(segment))
            : (ancestorNames[path] || labelForSegment(segment));
        crumbs.push({ name, path });
    });

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.path ? `${SITE_URL}/${crumb.path}` : SITE_URL,
        })),
    };
};

interface IArticleInput {
    slug: string;
    name?: string;
    excerpt?: string;
    summary?: string;
    coverImage?: string;
    author?: string;
    publishedDate?: string;
    updatedAt?: string;
    meta?: { title?: string; description?: string; imgUrl?: string; };
}

const asIsoDate = (value?: string) => {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

/**
 * Builds BlogPosting for an article. Google lists no required properties for
 * Article, so every field here is omitted rather than guessed when we do not
 * hold it: a fabricated date or author is worse than an absent one.
 */
export const buildArticle = (post: IArticleInput) => {
    const headline = toPlainText(post.meta?.title || post.name);
    if (!headline) return null;

    const url = `${SITE_URL}/blog/${post.slug}`;
    const description = toPlainText(post.meta?.description || post.excerpt || post.summary);
    const image = post.meta?.imgUrl || post.coverImage;
    const datePublished = asIsoDate(post.publishedDate);
    const dateModified = asIsoDate(post.updatedAt) || datePublished;
    const author = toPlainText(post.author);

    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
        headline,
        ...(description ? { description } : {}),
        ...(image ? { image: [image] } : {}),
        ...(datePublished ? { datePublished } : {}),
        ...(dateModified ? { dateModified } : {}),
        ...(author ? { author: { '@type': 'Person', name: author } } : {}),
        publisher: {
            '@type': 'Organization',
            name: 'Brackets Ninja',
            url: SITE_URL,
            logo: {
                '@type': 'ImageObject',
                url: 'https://website-assets.commoninja.com/distribution/1692691663291_og_brackets.png',
            },
        },
    };
};
